
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import Component from 'vue-class-component';
import moment, {MomentInput} from 'moment';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import settingx from '@/store/modules/settingx';
import Checkbox from 'primevue/checkbox';
import DateRangeFilter from '@/components/reports-v3/components/filters/DateRangeFilter.vue';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';

@Component({
    components: {
        TableCollapseToggle,
        DateRangeFilter,
        Checkbox,
        IncludeDebitNoteToggle,
        IncludeCreditNoteToggle,
        TableFilterable,
    },
})
export default class TiarcoChemicalMOtherIncomeBreakdownTable extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'month'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'project'
    > = ['suppliers', 'agents', 'dateAsOf', 'month', 'project'];

    public quantityToggle = false;
    public manualDateRange: [number, number] = [this.selectedDateRange[0], this.selectedDateRange[1]];
    public totalTableFields: object[] = [];
    public totalTableData: object[] = [];

    public get startDate() {
        return this.manualDateRange[0];
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public tableItems: object[] = [];
    public tableFields: object[] = [];

    public get currentYearRange(): [number, number] {
        return [
            moment(this.selectedAsOfDate).startOf('year').valueOf(),
            moment(this.selectedAsOfDate).endOf('month').valueOf(),
        ];
    }

    public getMonthYearArray(startDate, endDate) {
        const start = moment(startDate);
        const end = moment(endDate);

        const monthYearArray: string[] = [];

        while (start.isBefore(end) || start.isSame(end, 'month')) {
            monthYearArray.push(start.format('MMMYY'));
            start.add(1, 'month');
        }

        return monthYearArray;
    }

    public exportName(range: number[]) {
        return 'Tiarco Yearly Sales Analysis By Item Code' + ' from ' +
            moment(range[0] as MomentInput).format('DD MMM YY') +
            ' - ' +
            moment(range[1] as MomentInput).format('DD MMM YY');
    }

    public get expensiveHook() {
        const {
            manualDateRange,
            selectedDateRange,
            selectedAsOfDate,
            currentYearRange,
            selectedProjects,
            includeCreditNote,
            includeDebitNote,
            quantityToggle,
        } = this;
        return JSON.stringify([
            manualDateRange,
            selectedDateRange,
            selectedAsOfDate,
            currentYearRange,
            selectedProjects,
            includeCreditNote,
            includeDebitNote,
            quantityToggle,
        ]);
    }

    public async expensiveCalc() {
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const customerTable = getTableName('globalCustomers');
        const stockTable = getTableName('globalStocks');
        const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';
        const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
        const qty = 'TRY_CAST("QUANTITY" AS DOUBLE)';
        const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
        const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

        const accTypes = ['IV', 'CS'];
        if (this.includeDebitNote) { accTypes.push('DN'); }
        if (this.includeCreditNote) { accTypes.push('CN'); }
        const accTypesList = accTypes.map((t) => `'${t}'`).join(',');

        const range = this.manualDateRange;

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(
            ['sales', 'creditNotes', 'globalCustomers', 'globalStocks'],
        );
        this.checkInterrupted(jobId);

        this.generateLoadingText(0.3);

        // Parallel queries: sales, CN, customers, stocks
        const [salesResult, cnResult, customerResult, stockResult]
            = await Promise.all([
                queryHead.rawQuery(`
                    SELECT "CODE" as code, "ITEMCODE" as itemCode,
                        ${yearExpr} as year, ${monthExpr} as month,
                        SUM(${amt}) as total,
                        SUM(${qty}) as totalQty
                    FROM ${salesTable}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]} AND ${range[1]}
                        AND "DOCTYPE" IN (${accTypesList})
                    GROUP BY code, itemCode, year, month
                `),
                this.includeCreditNote
                    ? queryHead.rawQuery(`
                        SELECT "CODE" as code, "ITEMCODE" as itemCode,
                            ${yearExpr} as year, ${monthExpr} as month,
                            SUM(${amt}) as total,
                            SUM(${qty}) as totalQty
                        FROM ${cnTable}
                        WHERE ${dateExpr}
                            BETWEEN ${range[0]} AND ${range[1]}
                        GROUP BY code, itemCode, year, month
                    `)
                    : Promise.resolve([]),
                queryHead.rawQuery(`
                    SELECT "CODE", "COMPANYNAME"
                    FROM ${customerTable}
                `),
                queryHead.rawQuery(`
                    SELECT "CODE", "DESCRIPTION"
                    FROM ${stockTable}
                `),
            ]);
        this.checkInterrupted(jobId);

        // Build customer/stock lookups
        const customerMap: Record<string, string> = {};
        for (const row of customerResult) {
            customerMap[row.CODE] = row.COMPANYNAME;
        }
        const stockMap: Record<string, string> = {};
        for (const row of stockResult) {
            stockMap[row.CODE] = row.DESCRIPTION;
        }

        // Build month labels
        const allMonthYears = this.getMonthYearArray(range[0], range[1]);

        // Build sales lookup: code-itemCode-MMMYY → {total, qty}
        const salesTab: Record<string, { total: number; qty: number }> = {};
        const pairSet = new Set<string>();
        for (const row of salesResult) {
            const m = moment().year(Number(row.year))
                .month(Number(row.month) - 1);
            const my = m.format('MMMYY');
            const pairKey = `${row.code}\t${row.itemCode}`;
            pairSet.add(pairKey);
            const key = `${pairKey}-${my}`;
            if (!salesTab[key]) {
                salesTab[key] = { total: 0, qty: 0 };
            }
            salesTab[key].total += Number(row.total) || 0;
            salesTab[key].qty += Number(row.totalQty) || 0;
        }

        // Build CN lookup
        const cnTab: Record<string, { total: number; qty: number }> = {};
        for (const row of cnResult) {
            const m = moment().year(Number(row.year))
                .month(Number(row.month) - 1);
            const my = m.format('MMMYY');
            const key = `${row.code}\t${row.itemCode}-${my}`;
            if (!cnTab[key]) {
                cnTab[key] = { total: 0, qty: 0 };
            }
            cnTab[key].total += Number(row.total) || 0;
            cnTab[key].qty += Number(row.totalQty) || 0;
        }

        this.generateLoadingText(0.7);

        // Build table items
        const groupData: any[] = [];
        const tempTotalData: any = { month: 'Total' };
        allMonthYears.forEach((my) => { tempTotalData[my] = 0; });

        const useQty = this.quantityToggle;
        const valFmt = !useQty
            ? TableItemFormatter.currencySplit
            : TableItemFormatter.basicWithDecimals;

        pairSet.forEach((pairKey) => {
            const [code, itemCode] = pairKey.split('\t');
            const item: any = {
                company_name: customerMap[code] || code,
                item_code: itemCode,
                item_desc: stockMap[itemCode] || itemCode,
                total_value: 0,
            };

            allMonthYears.forEach((my) => {
                const sKey = `${pairKey}-${my}`;
                const sVal = salesTab[sKey];
                let val = sVal
                    ? (useQty ? sVal.qty : sVal.total) : 0;

                // Subtract CN if applicable
                if (this.includeCreditNote) {
                    const cKey = `${pairKey}-${my}`;
                    const cVal = cnTab[cKey];
                    if (cVal) {
                        val -= useQty ? cVal.qty : cVal.total;
                    }
                }

                item[my] = val;
                item.total_value += val;
                tempTotalData[my] += val;
            });

            groupData.push(item);
        });

        // Build table fields
        this.tableFields = [
            { key: 'company_name', sortable: false, formatter: undefined },
            { key: 'item_code', sortable: false, formatter: undefined },
            { key: 'item_desc', sortable: false, formatter: undefined },
            ...allMonthYears.map((my) => ({
                key: my,
                sortable: false,
                formatter: valFmt,
            })),
            { key: 'total_value', sortable: false, formatter: valFmt },
        ];

        this.totalTableFields = [
            {
                key: 'month',
                sortable: false,
                formatter: undefined,
                textAlign: 'center',
            },
            ...allMonthYears.map((my) => ({
                key: my,
                sortable: false,
                formatter: valFmt,
                textAlign: 'center',
            })),
        ];

        this.totalTableData = [tempTotalData];
        this.tableItems = groupData;

        this.generateLoadingText(1);

        this.saveHistory('tableItems', 'tableFields', 'totalTableData', 'totalTableFields');
    }


}
